import { useMemo, useState } from "react";
import { Search, Plus, Sparkles } from "lucide-react";
import { glass, BURNT, BURNT_GLOW, GREEN, ORANGE } from "./shared";
import { toast } from "sonner";

type Term = {
  term: string;
  clicks: number;
  impr: number;
  cpc: number;
  conv: number;
  intent: number;
  matchType: "Broad" | "Phrase" | "Exact";
  campaign: string;
};

const TERMS: Term[] = [
  { term: "ai google ads agency", clicks: 412, impr: 8420, cpc: 1.84, conv: 24, intent: 96, matchType: "Phrase", campaign: "Lead Gen Search" },
  { term: "automated ppc software", clicks: 308, impr: 6210, cpc: 1.42, conv: 18, intent: 88, matchType: "Broad", campaign: "Lead Gen Search" },
  { term: "best google ads tool 2026", clicks: 224, impr: 4810, cpc: 2.1, conv: 14, intent: 92, matchType: "Phrase", campaign: "Brand Awareness Q2" },
  { term: "google ads optimization service", clicks: 194, impr: 3920, cpc: 2.4, conv: 11, intent: 90, matchType: "Exact", campaign: "Lead Gen Search" },
  { term: "ppc agency near me", clicks: 168, impr: 4210, cpc: 1.62, conv: 7, intent: 78, matchType: "Phrase", campaign: "Local Service Ads" },
  { term: "ai for google ads", clicks: 142, impr: 3120, cpc: 1.94, conv: 10, intent: 91, matchType: "Broad", campaign: "Lead Gen Search" },
  { term: "free google ads audit", clicks: 128, impr: 2840, cpc: 0.98, conv: 6, intent: 72, matchType: "Phrase", campaign: "Brand Awareness Q2" },
  { term: "google ads vs facebook ads", clicks: 96, impr: 5210, cpc: 0.42, conv: 1, intent: 38, matchType: "Broad", campaign: "Brand Awareness Q2" },
  { term: "what is roas", clicks: 84, impr: 4810, cpc: 0.32, conv: 0, intent: 18, matchType: "Broad", campaign: "Brand Awareness Q2" },
  { term: "orbitrank reviews", clicks: 72, impr: 1240, cpc: 0.6, conv: 5, intent: 84, matchType: "Exact", campaign: "Brand Awareness Q2" },
];

const SearchTermsTab = ({
  mode,
  onTriggerAria,
}: {
  mode: "standard" | "advanced";
  onTriggerAria: (prompt: string, ctx: Record<string, any>) => void;
}) => {
  const [query, setQuery] = useState("");
  const [match, setMatch] = useState("all");
  const [campaign, setCampaign] = useState("all");
  const [convOnly, setConvOnly] = useState("all");
  const [bubble, setBubble] = useState<string | null>(null);

  const campaigns = useMemo(() => Array.from(new Set(TERMS.map((t) => t.campaign))), []);

  const rows = useMemo(() => {
    return TERMS.filter((t) => {
      if (query && !t.term.includes(query.toLowerCase())) return false;
      if (match !== "all" && t.matchType !== match) return false;
      if (campaign !== "all" && t.campaign !== campaign) return false;
      if (convOnly === "yes" && t.conv === 0) return false;
      if (convOnly === "no" && t.conv > 0) return false;
      return true;
    });
  }, [query, match, campaign, convOnly]);

  const handleAdd = (t: Term) => {
    if (mode === "standard") {
      setBubble(t.term);
      setTimeout(() => setBubble(null), 3500);
      return;
    }
    onTriggerAria(`Add "${t.term}" as a keyword`, {
      searchTerm: t.term,
      matchType: t.matchType,
      adGroup: `${t.campaign} › High Intent`,
      impact: Math.round(t.intent / 5),
    });
    toast.success(`ARIA is preparing "${t.term}"`);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search terms…"
            className="h-9 w-64 rounded-lg border border-white/10 bg-white/[0.03] pl-8 pr-3 text-[12.5px] text-white placeholder:text-white/30 focus:border-white/25 focus:outline-none"
          />
        </div>
        <select
          value={match}
          onChange={(e) => setMatch(e.target.value)}
          className="h-9 rounded-lg border border-white/10 bg-white/[0.03] px-3 text-[12.5px] text-white focus:outline-none"
        >
          <option value="all">All match types</option>
          <option value="Broad">Broad</option>
          <option value="Phrase">Phrase</option>
          <option value="Exact">Exact</option>
        </select>
        <select
          value={campaign}
          onChange={(e) => setCampaign(e.target.value)}
          className="h-9 rounded-lg border border-white/10 bg-white/[0.03] px-3 text-[12.5px] text-white focus:outline-none"
        >
          <option value="all">All campaigns</option>
          {campaigns.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={convOnly}
          onChange={(e) => setConvOnly(e.target.value)}
          className="h-9 rounded-lg border border-white/10 bg-white/[0.03] px-3 text-[12.5px] text-white focus:outline-none"
        >
          <option value="all">Any conversions</option>
          <option value="yes">With conversions</option>
          <option value="no">Zero conversions</option>
        </select>
      </div>

      {/* Table */}
      <div data-tour-id="search-terms-table" className="overflow-hidden rounded-2xl" style={glass}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-left text-[12.5px]">
            <thead className="bg-white/[0.02]">
              <tr>
                {["Search Term", "Clicks", "Impressions", "CPC", "Conversions", "Intent Score", "Action"].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold uppercase tracking-[0.1em] text-[10.5px] text-white/45">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.term} className="border-t border-white/5 hover:bg-white/[0.025]">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-white">{t.term}</div>
                    <div className="text-[10.5px] text-white/45">
                      {t.matchType} · {t.campaign}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white/80">{t.clicks.toLocaleString()}</td>
                  <td className="px-4 py-3 text-white/80">{t.impr.toLocaleString()}</td>
                  <td className="px-4 py-3 text-white/80">${t.cpc.toFixed(2)}</td>
                  <td className="px-4 py-3 font-semibold" style={{ color: t.conv > 0 ? GREEN : "rgba(255,255,255,0.55)" }}>
                    {t.conv}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-white/8">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${t.intent}%`,
                            background:
                              t.intent >= 80
                                ? `linear-gradient(90deg, ${BURNT}, ${BURNT_GLOW})`
                                : t.intent >= 50
                                  ? ORANGE
                                  : "rgba(255,255,255,0.3)",
                          }}
                        />
                      </div>
                      <span className="text-[11px] font-bold text-white/75">{t.intent}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="relative">
                      <button
                        onClick={() => handleAdd(t)}
                        className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-[11.5px] font-bold text-white transition-transform hover:scale-[1.03] active:scale-[0.97]"
                        style={{
                          background: `${BURNT}1f`,
                          border: `1px solid ${BURNT}55`,
                          color: BURNT_GLOW,
                        }}
                      >
                        <Plus size={11} />
                        Add as Keyword
                      </button>
                      {bubble === t.term && (
                        <div
                          className="animate-fade-in absolute right-0 top-full z-30 mt-2 w-72 rounded-xl p-3 text-[11.5px] text-white"
                          style={{ ...glass, border: `1px solid ${BURNT}55` }}
                        >
                          <div className="mb-1 flex items-center gap-1.5 font-bold" style={{ color: BURNT_GLOW }}>
                            <Sparkles size={11} /> ARIA
                          </div>
                          Advanced mode is required to add keywords directly. Switch ARIA to Advanced mode to push this live.
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-white/40">
                    No search terms match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SearchTermsTab;
